// Fixed teams.ts with proper authentication handling and random avatars
// Replace your getUserTeams function with this version

import { supabase } from '../../../shared/lib/supabase';
import type { Database } from '../../../shared/lib/database.types';

// Basic types
type Team = Database['public']['Tables']['teams']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];

// Extended types
export interface TeamWithMembers extends Team {
  member_count: number;
  is_owner: boolean;
  team_members?: (TeamMember & {
    user_profiles: Database['public']['Tables']['user_profiles']['Row'];
  })[];
}

export interface CreateTeamParams {
  name: string;
}

export interface TeamPermissions {
  canInviteMembers: boolean;
  canRemoveMembers: boolean;
  canDeleteTeam: boolean;
  canEditTeam: boolean;
  canCreateProjects: boolean;
  canViewProjects: boolean;
}

// =============================================
// RANDOM AVATAR FUNCTION
// =============================================

export function getRandomTeamAvatar(): string {
  const avatarNumber = Math.floor(Math.random() * 5) + 1;
  return `https://nnpzyoxebibftvsrqccv.supabase.co/storage/v1/object/public/team-avatars/BaseAvatar${avatarNumber}.png`;
}

// =============================================
// FIXED getUserTeams FUNCTION
// =============================================

export async function getUserTeams(): Promise<TeamWithMembers[]> {
  
  try {
    // Get current user with better error handling
    const userResponse = await supabase.auth.getUser();
    
    // FIXED: Don't throw error immediately, return empty array for loading states
    if (!userResponse.data.user) {
      // Check if this is a session missing error (common during auth flow)
      if (userResponse.error) {
        // Don't throw error for session missing - this is normal during auth flow
        if (userResponse.error.message?.includes('Auth session missing')) {
          return [];
        }
        
        // Only throw for other auth errors
        throw new Error('Authentication error');
      }
      return [];
    }
    
    const userId = userResponse.data.user.id;
    
    // Get team memberships
    const membershipResponse = await supabase
      .from('team_members')
      .select('team_id, role')
      .eq('user_id', userId);
    
    if (membershipResponse.error) {
      throw membershipResponse.error;
    }
    
    const memberships = membershipResponse.data || [];
    
    if (memberships.length === 0) {
      return [];
    }
    
    // Get all team IDs from memberships
    const teamIds = memberships.map(m => m.team_id);
    
    // Batch fetch all teams in parallel (2 queries instead of N+M queries)
    const [teamsResponse, memberCountsResponse] = await Promise.all([
      supabase
        .from('teams')
        .select('*')
        .in('id', teamIds),
      supabase
        .from('team_members')
        .select('team_id')
        .in('team_id', teamIds)
    ]);
    
    if (teamsResponse.error) {
      console.error('Error fetching teams:', teamsResponse.error);
      throw teamsResponse.error;
    }
    
    if (memberCountsResponse.error) {
      console.error('Error fetching member counts:', memberCountsResponse.error);
      throw memberCountsResponse.error;
    }
    
    // Create a map of team_id to member count for fast lookup
    const memberCountMap = (memberCountsResponse.data || []).reduce((acc, member) => {
      acc[member.team_id] = (acc[member.team_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Create a map of team_id to role for fast lookup
    const roleMap = memberships.reduce((acc, membership) => {
      acc[membership.team_id] = membership.role;
      return acc;
    }, {} as Record<string, string>);
    
    // Combine teams with their member counts and user roles
    const teams: TeamWithMembers[] = (teamsResponse.data || []).map(teamData => ({
      ...teamData,
      member_count: memberCountMap[teamData.id] || 1,
      is_owner: roleMap[teamData.id] === 'owner'
    }));
    
    return teams;
    
  } catch (error) {
    console.error('getUserTeams error:', error);
    throw error;
  }
}

// =============================================
// UPDATED createTeam FUNCTION WITH RANDOM AVATAR
// =============================================

export async function createTeam({ name }: CreateTeamParams): Promise<Team> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') + '-' + Date.now();

  const { data, error } = await supabase
    .from('teams')
    .insert({
      name,
      owner_id: user.user.id,
      slug,
      is_personal: false,
      avatar_url: getRandomTeamAvatar(), // ADD THIS LINE - assigns random avatar
    })
    .select();

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Failed to create team');

  const team = data[0];

  await supabase
    .from('team_members')
    .insert({
      team_id: team.id,
      user_id: user.user.id,
      role: 'owner'
    });

  return team;
}

// =============================================
// REST OF YOUR FUNCTIONS (unchanged)
// =============================================

export async function getTeamProjects(teamId: string) {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('team_id', teamId);

  if (error) throw error;
  return data || [];
}

export async function createProjectInTeam(
  teamId: string,
  projectData: {
    name: string;
    description?: string;
    workflow_type: 'enzyme' | 'antibody' | 'peptide';
  }
) {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('projects')
    .insert({
      ...projectData,
      team_id: teamId,
      user_id: user.user.id,
    })
    .select();

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Failed to create project');
  return data[0];
}

export function isPersonalTeam(team: Team): boolean {
  return team.is_personal;
}

export function getPersonalTeam(teams: TeamWithMembers[]): TeamWithMembers | null {
  return teams.find(team => team.is_personal) || null;
}

export function getNonPersonalTeams(teams: TeamWithMembers[]): TeamWithMembers[] {
  return teams.filter(team => !team.is_personal);
}

export function formatTeamName(team: Team): string {
  return team.is_personal ? 'Personal' : team.name;
}

export function getTeamPermissions(team: TeamWithMembers): TeamPermissions {
  return {
    canInviteMembers: team.is_owner,
    canRemoveMembers: team.is_owner,
    canDeleteTeam: team.is_owner && !team.is_personal,
    canEditTeam: team.is_owner,
    canCreateProjects: true,
    canViewProjects: true,
  };
}

export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('team_id', teamId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function deleteTeam(teamId: string): Promise<void> {
  const { error } = await supabase
    .from('teams')
    .delete()
    .eq('id', teamId);

  if (error) throw error;
}